import { useLayoutEffect, useRef, type CSSProperties, type ReactNode } from 'react';

/**
 * Text that is never cut. It wraps to `lines` lines at the size its classes
 * give it; when it needs more, it steps down 1px at a time, line height in
 * proportion, until it fits or reaches `minPx` (Caption 2, 11, by default).
 * Past the floor it takes the lines it needs rather than clip: no ellipsis,
 * no overflow hidden, at any width. A long word breaks rather than spill.
 *
 * Re-measured whenever its box changes width, so a rotated phone or a
 * resized pane fits again. For a single line (a chip, a button), pass
 * `lines={1}`.
 */
export function FitText({
  children, lines = 2, minPx = 11, className = '', style, as: Tag = 'span', title,
}: {
  children: ReactNode;
  lines?: number;
  minPx?: number;
  className?: string;
  style?: CSSProperties;
  as?: 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3';
  title?: string;
}) {
  const ref = useRef<HTMLElement | null>(null);
  // The caller's own size, if it set one in `style`: the reset goes back to it, not past it.
  const px = (v: CSSProperties['fontSize']) => (typeof v === 'number' ? `${v}px` : v ?? '');
  const baseFont = px(style?.fontSize);
  const baseLine = px(style?.lineHeight as CSSProperties['fontSize']);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let lastWidth = -1;
    const fit = () => {
      const width = el.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      // Back to the size the classes give, then measure from there.
      el.style.fontSize = baseFont;
      el.style.lineHeight = baseLine;
      const cs = getComputedStyle(el);
      const base = parseFloat(cs.fontSize);
      const lh = parseFloat(cs.lineHeight);
      const ratio = Number.isFinite(lh) && base > 0 ? lh / base : 1.25;
      const fits = (size: number) => el.scrollHeight <= Math.ceil(size * ratio * lines) + 1;
      let size = base;
      while (size > minPx && !fits(size)) {
        size -= 1;
        el.style.fontSize = `${size}px`;
        el.style.lineHeight = `${Math.round(size * ratio * 100) / 100}px`;
      }
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children, lines, minPx, className, baseFont, baseLine]);

  return (
    <Tag
      ref={ref as never}
      title={title}
      style={style}
      className={`block min-w-0 [overflow-wrap:anywhere] ${className}`}
    >
      {children}
    </Tag>
  );
}
